import {
  type Locale,
  getLanguageFromLocale,
  getZoneFromSite,
  siteConfig,
} from '@seek/melways-sites';
import type { EmployerSiteNames, Environment } from 'src/siteConfig';
import type { TrackJs } from 'src/utils/window';
import { type Analytics, createTealiumAdapter } from './createTealiumAdapter';
import { getBrandFromSiteForAnalytics } from './getBrandFromSiteForAnalytics';
import type { SortOptionValue } from '../SortOrderDropdown';

export type AnalyticsDocumentType =
  | 'cover letter'
  | 'resume'
  | 'selection criteria';

type YourCandidateLinkAnalyticEvents =
  | {
      event: 'your_candidate_tab_sort_option_pressed';
      attributes: {
        sortMode: SortOptionValue;
      };
    }
  | {
      event: 'your_candidate_tab_previous_apps_pressed';
      attributes: {
        jobId: number;
        seekCandidateId: number;
        adcentreCandidateId: number;
        pageNumber: number;
      };
    }
  | {
      event: 'your_candidate_tab_candidate_name_pressed';
      attributes: {
        jobId: number;
        seekCandidateId: number;
        adcentreCandidateId: number;
        pageNumber: number;
      };
    }
  | {
      event: 'your_candidate_tab_attachment_download_pressed';
      attributes: {
        pageNumber: number;
        jobId: string | number;
        seekCandidateId: string | number;
        adcentreCandidateId: string | number;
        prospectId: string | number;
        attachmentId: string | number;
        documentType: AnalyticsDocumentType;
      };
    }
  | {
      event: 'your_candidate_tab_email_candidate_pressed';
      attributes: {
        jobId: number;
        seekCandidateId: number;
        adcentreCandidateId: number;
        pageNumber: number;
      };
    }
  | {
      event: 'your_candidate_tab_search_button_pressed';
      attributes: {
        searchKeyword: string;
      };
    }
  | {
      event: 'pagination_numbered_page_btn_pressed';
      attributes: {
        pageNumber: number;
        isLastPage: boolean;
      };
    }
  | {
      event: 'pagination_next_page_btn_pressed';
      attributes: {
        pageNumber: number;
        isLastPage: boolean;
      };
    }
  | {
      event: 'pagination_previous_page_btn_pressed';
      attributes: {
        pageNumber: number;
        isLastPage: boolean;
      };
    };

type YourCandidatesViewAnalyticEvents =
  | {
      event: 'your_candidate_tab_displayed';
      attributes: {
        currentPage: 'your_candidate_tab_displayed';
        pageNumber: number;
        sortMode: SortOptionValue;
        isCandidateListFiltered: boolean;
      };
    }
  | {
      event: 'your_candidate_tab_zero_result_displayed';
      attributes: {
        currentPage: 'your_candidate_tab_zero_result_displayed';
        isCandidateListFiltered: boolean;
      };
    }
  | {
      event: 'error_page_displayed';
      attributes: {
        currentPage: 'error_page_displayed';
      };
    };

export type YourCandidatesPageAnalytics = Analytics<
  YourCandidateLinkAnalyticEvents,
  YourCandidatesViewAnalyticEvents
>;

export type CreatePageAnalytics = (params: {
  locale: Locale;
  site: EmployerSiteNames;
  userId: number | null;
  seekAdvertiserId: string | null;
  trackJs: TrackJs | null;
  environment: Environment;
}) => YourCandidatesPageAnalytics;

export const createPageAnalytics: CreatePageAnalytics = ({
  site,
  locale,
  userId,
  seekAdvertiserId,
  trackJs,
  environment,
}) => {
  const isDev = environment === 'dev';
  const brand = getBrandFromSiteForAnalytics({ site, trackJs });
  return createTealiumAdapter({
    debug: isDev,
    baseAttributes: {
      siteSection: 'jobs',
      siteSubsection: 'your candidate',
      brand,
      isLoggedIn: true,
      zone: getZoneFromSite(site),
      siteLanguage: getLanguageFromLocale(locale),
      siteCountry: siteConfig[site].classification.country,
      userId,
      seekAdvertiserId,
    },
  });
};
